import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook that automatically applies referral code from user metadata after login
 * This version uses a database function to bypass RLS issues
 * This runs once when user session is established
 */
export function useAutoApplyReferral() {
  const { user, session } = useAuth();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (!user || !session || hasAttempted.current) {
      return;
    }

    const applyReferralFromMetadata = async () => {
      try {
        console.log('=== AUTO APPLY REFERRAL START ===');
        console.log('Auth user id:', user.id);
        console.log('User metadata:', user.user_metadata);

        // Get referral code from user metadata
        const metadata = user.user_metadata;
        const referralCodeToApply = metadata?.referral_code_to_apply;

        if (!referralCodeToApply) {
          console.log('❌ No referral code in metadata, skipping');
          return;
        }

        console.log('✅ Found referral code in metadata:', referralCodeToApply);

        const userType = metadata?.user_type || 'client';
        console.log('User type:', userType);

        // Call the database function to apply referral
        const { data, error } = await supabase.rpc('apply_referral_code', {
          p_referral_code: referralCodeToApply,
          p_user_type: userType
        });

        console.log('Database function result:', { data, error });

        if (error) {
          console.error('❌ Error calling apply_referral_code:', error);
          return;
        }

        // Type assertion for the RPC result
        const result = data as { success?: boolean; error?: string } | null;

        if (result?.success) {
          console.log('✅ Referral applied successfully via database function');
          console.log('Result:', result);
        } else {
          console.warn('⚠️ Database function returned failure:', result?.error);
        }
      } catch (err) {
        console.error('❌ FATAL Error in auto-apply referral:', err);
        console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      } finally {
        console.log('=== AUTO APPLY REFERRAL END ===');
      }
    };

    hasAttempted.current = true;
    applyReferralFromMetadata();
  }, [user, session]);
}
